import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  checkEnvioFlexAccess,
  checkFulfillAccess,
  checkMLFulfillAccess,
  checkMLParcelAccess,
  getBalance,
  getProfile,
} from '@/redux/parcel/asyncRequest';
import { AppDispatch, RootState } from '@/redux/store';
import { getUPSProfile } from '@/redux/ups/upsSlice';

import type { PropsWithChildren } from 'react';

import { LoadingPage } from './pages/loading';
import { Auth0WithoutPermission } from './pages/restricted';

export const AuthRequired = (props: PropsWithChildren) => {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();
  const dispatch = useDispatch<AppDispatch>();
  const [fetchingProfile, setFetchingProfile] = useState(true);
  const hasFetched = useRef(false);
  const { profile } = useSelector((state: RootState) => state.parcel);

  useEffect(() => {
    if (!isLoading) {
      const errorMessage = error?.message ?? '';
      const isSessionError =
        errorMessage.includes('login_required') ||
        errorMessage.includes('invalid_grant') ||
        errorMessage.includes('consent_required') ||
        errorMessage.includes('Missing Refresh Token') ||
        errorMessage.includes('Could not verify JWT');
      const shouldRedirectToLogin = (!isAuthenticated && !error) || isSessionError;

      if (shouldRedirectToLogin) {
        loginWithRedirect({
          appState: {
            returnTo: window.location.pathname + window.location.search,
          },
        });
      }
    }
  }, [isAuthenticated, error, isLoading, loginWithRedirect]);

  useEffect(() => {
    if (!isAuthenticated || isLoading || error) {
      return;
    }

    if (hasFetched.current || profile.data) {
      setFetchingProfile(false);
      return;
    }

    hasFetched.current = true;

    const fetchData = async () => {
      try {
        const profileResult = await dispatch(getProfile());

        const promises: Promise<unknown>[] = [dispatch(getUPSProfile())];

        if (
          profileResult.payload &&
          getProfile.fulfilled.match(profileResult) &&
          profileResult.payload.data.configuration.paymentType === 'PREPAID'
        ) {
          promises.push(dispatch(getBalance()));
        }

        if (
          profileResult.payload &&
          getProfile.fulfilled.match(profileResult) &&
          profileResult.payload.data.configuration.services.fulfill99
        ) {
          promises.push(dispatch(checkFulfillAccess()));
        }

        if (profileResult.payload && getProfile.fulfilled.match(profileResult)) {
          promises.push(dispatch(checkEnvioFlexAccess()));
          promises.push(dispatch(checkMLFulfillAccess()));
          promises.push(dispatch(checkMLParcelAccess()));
        }

        await Promise.all(promises);
      } catch (err) {
        console.error('Error fetching initial user profile:', err);
      } finally {
        setFetchingProfile(false);
      }
    };

    fetchData();
  }, [isAuthenticated, isLoading, error, dispatch, profile.data]);

  if (
    isLoading ||
    (!isAuthenticated && !isLoading && !error) ||
    (isAuthenticated && fetchingProfile)
  ) {
    return <LoadingPage />;
  }

  if (error) {
    return <Auth0WithoutPermission />;
  }

  return <>{props.children}</>;
};
